import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';

import { COUNTRIES_LIST } from '~/components/Common/CustomOffers/constants';

import {
  useGetBusiness,
  useGetBusinessSummary,
  useUpdateBusiness,
  useUpdateBusinessSummary,
} from '~/hooks/apis/business-traveller/useBusinessDetails';

interface Params {
  businessId: string;
  tab: string;
}

const emptyBusiness: App.Business = {
  country: '',
  businessIdentifier: '',
  employeeCount: '',
  id: '',
  industry: '',
  name: '',
  phone: '',
  enableMerchantFees: false,
};

const industries = {
  accommodation: 'Accommodation and food services',
  administration: 'Administrative and support services',
  agriculture: 'Agriculture, forestry and fishing',
  arts: 'Arts and recreation services',
  construction: 'Construction',
  education: 'Education and training',
  finance: 'Financial and insurance services',
  health: 'Health care and social assistance',
  manufacturing: 'Manufacturing',
  mining: 'Mining',
  other: 'Other',
  publicadmin: 'Public administration and safety',
  realestate: 'Rental, hiring and real estate services',
  retail: 'Retail or wholesale trade',
  science: 'Professional, scientific and technical services',
  telecommunications: 'Information media and telecommunications',
  transport: 'Transport, postal and warehousing',
  utilities: 'Electricity, gas, water and waste services',
};

const businessIdentifierMap = {
  australian_business_number: 'ABN',
  default: 'ABN',
};

const BUSINESS_TRAVELLER_BUSINESS_EMPLOYEE_COUNT_OPTIONS = [
  ['1-20', '1-20'],
  ['21-50', '21-50'],
  ['51-200', '51-200'],
  ['200+', '200+'],
];

const BUSINESS_SUMMARY_MAX_CHARS = 3000;

export default function BusinessTab() {
  const [business, setBusiness] = useState<App.Business>(emptyBusiness);
  const [businessSummary, setBusinessSummary] = useState('');
  const { businessId } = useParams<keyof Params & string>();
  const { enqueueSnackbar } = useSnackbar();

  const { data: businessData, isLoading: isLoadingBusiness, error: businessError } = useGetBusiness(businessId);
  const { data: summaryData, isLoading: isLoadingSummary, error: summaryError } = useGetBusinessSummary(businessId);
  const updateBusinessMutation = useUpdateBusiness(businessId);
  const updateSummaryMutation = useUpdateBusinessSummary(businessId);

  useEffect(() => {
    if (businessData) {
      setBusiness(businessData);
    }
  }, [businessData]);

  useEffect(() => {
    if (summaryData !== undefined) {
      setBusinessSummary(summaryData);
    }
  }, [summaryData]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;

    if (type === 'checkbox') {
      setBusiness((prevState) => ({ ...prevState, [name]: checked }));
    } else {
      setBusiness((prevState) => ({ ...prevState, [name]: value }));
    }
  };

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      await Promise.all([
        updateBusinessMutation.mutateAsync(business),
        updateSummaryMutation.mutateAsync(businessSummary),
      ]);
      enqueueSnackbar('Business updated successfully', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(`Error patching business [${JSON.stringify(e, null, 2)}]`, {
        variant: 'error',
      });
    }
  }

  if (isLoadingBusiness || isLoadingSummary) {
    return <CircularProgress />;
  }

  return (
    <Box sx={{ maxWidth: 800 }}>
      <form onSubmit={onSubmit}>
        <Stack gap={2}>
          {businessError && <Alert severity="error">Error fetching business with id {businessId}</Alert>}
          {summaryError && <Alert severity="error">Error fetching business summary</Alert>}

          <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
            Business Information
          </Typography>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" gutterBottom>
                Business Name
              </Typography>
              <FormControl fullWidth>
                <OutlinedInput
                  id="business_name"
                  name="name"
                  onChange={handleChange}
                  placeholder="Enter name of business"
                  value={business.name}
                  sx={{ mt: 1 }}
                />
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" gutterBottom>
                Phone Number
              </Typography>
              <FormControl fullWidth>
                <OutlinedInput
                  id="business_phone"
                  name="phone"
                  onChange={handleChange}
                  placeholder="Enter phone number"
                  value={business.phone}
                  sx={{ mt: 1 }}
                />
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" gutterBottom>
                Country
              </Typography>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <Select
                  value={business.country}
                  id="business_country"
                  name="country"
                  onChange={handleChange}
                  displayEmpty
                >
                  {COUNTRIES_LIST.map((country) => (
                    <MenuItem value={country} key={country}>
                      {country}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" gutterBottom>
                Industry
              </Typography>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <Select
                  value={business.industry}
                  id="business_industry"
                  name="industry"
                  onChange={handleChange}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    <em>Select an industry</em>
                  </MenuItem>
                  {Object.keys(industries).map((industry) => (
                    <MenuItem key={industry} value={industry}>
                      {industries[industry]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" gutterBottom>
                Number of Employees
              </Typography>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <Select
                  value={business.employeeCount}
                  id="business_employee_count"
                  name="employeeCount"
                  onChange={handleChange}
                  displayEmpty
                >
                  {BUSINESS_TRAVELLER_BUSINESS_EMPLOYEE_COUNT_OPTIONS.map(([key, label]) => (
                    <MenuItem value={key} key={key}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" gutterBottom>
                {businessIdentifierMap[business.businessIdentifierType || 'default']}
              </Typography>
              <FormControl fullWidth>
                <OutlinedInput
                  id="business_identifier"
                  name="businessIdentifier"
                  onChange={handleChange}
                  placeholder={`Enter ${businessIdentifierMap[business.businessIdentifierType || 'default']}`}
                  value={business.businessIdentifier}
                  sx={{ mt: 1 }}
                />
              </FormControl>
            </Grid>

            <Grid size={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                Referral Information
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" gutterBottom>
                Referral Code
              </Typography>
              <Typography>{business.referralCode || 'Not available'}</Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" gutterBottom>
                Referral Email
              </Typography>
              <Typography> {business.referralEmail || 'Not available'}</Typography>
            </Grid>

            <Grid size={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                Settings
              </Typography>
            </Grid>

            <Grid size={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="enable_merchant_fees"
                      name="enableMerchantFees"
                      checked={business.enableMerchantFees}
                      onChange={handleChange}
                    />
                  }
                  label="Enable merchant fees"
                />
              </Box>
            </Grid>

            <Grid size={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Business Summary
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Include a summary of the business and its operations. This will be shown to support agents
              </Typography>
              <TextField
                id="business_summary"
                multiline
                minRows={4}
                fullWidth
                placeholder="Enter a summary of the business"
                value={businessSummary}
                onChange={(e) => setBusinessSummary(e.target.value)}
                slotProps={{ htmlInput: { maxLength: BUSINESS_SUMMARY_MAX_CHARS } }}
                helperText={`${businessSummary.length}/${BUSINESS_SUMMARY_MAX_CHARS} characters`}
                disabled={!!summaryError}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              sx={{ px: 4 }}
              disabled={updateBusinessMutation.isPending || updateSummaryMutation.isPending}
            >
              Save Changes
            </Button>
          </Box>
        </Stack>
      </form>
    </Box>
  );
}
